import { DataLayer } from '@piwikpro/react-piwik-pro';

import {
  useAuthenticationStore,
  type AuthModalType,
} from '$/components/core/Authentication/stores/useAuthenticationStore';
import { t } from '$/components/core/Localization/18n';
import { useLocalizationStore } from '$/components/core/Localization/hooks/useLocalizationStore';
import { trackEvent } from '$/logger';
import { useDesignGuideStore } from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { useInspirationEditorStore } from '$/pages/InspirationEditorPage/stores/useInspirationEditorStore';
import { type TypedToOptions } from '$/routes';
import {
  type MaterialType,
  type Material,
} from '$/services/usecases/materials/mapper/materials';
import { useFavoriteStore } from '$/stores/useFavoriteStore';
import { type PiwikDesignOption, usePiwikStore } from '$/stores/usePiwikStore';
import { capitalizeSentence } from '$/utils/stringUtils';

export type PiwikEventCategory =
  | 'FilterCollections'
  | 'FilterInspiration'
  | 'FilterDesignGuideEXT'
  | 'FilterDesignGuideINT'
  | 'ColorTone'
  | 'WallCovering'
  | 'FloorCovering'
  | 'FacadeCovering'
  | 'Projects'
  | 'Favorites'
  | 'Schools'
  | 'Harmony'
  | 'Tools-DesignGuide'
  | 'Tools-Inspiration'
  | 'Tools-Editor'
  | 'Renderings';

export type PiwikEventAction =
  | 'Producer'
  | 'Collection'
  | 'Mood'
  | 'Tonality'
  | 'Colorgroup'
  | 'BuildingType'
  | 'RoomType'
  | 'LightReferenceValueMin'
  | 'LightReferenceValueMax'
  | 'Productgroup'
  | 'Floor_Colorintensity'
  | 'Floor_Design'
  | 'Floor_Material'
  | 'Floor_Use'
  | 'Wallpaper_Colorintensity'
  | 'Wallpaper_Design'
  | 'Click'
  | 'AddFavorite'
  | 'Roomtype'
  | 'CreateNew'
  | 'NewFolder'
  | 'NewClass'
  | 'InviteStudent'
  | 'NewStudent'
  | 'ClassShare'
  | 'Fullscreen'
  | 'Save'
  | 'Share'
  | 'ShareInvite'
  | 'ShareLink'
  | 'Export'
  | 'ExportButton'
  | 'Inspiration'
  | 'Editor';

export type PiwikContentInteraction =
  | 'ClickImage'
  | 'ClickPreview'
  | 'ClickButton'
  | 'ClickVariants'
  | 'ClickPerspective';

export type PiwikContentName =
  | 'CollectionOverlay'
  | 'CloudAccount'
  | 'SchoolAccount'
  | 'School'
  | 'Favorite'
  | 'Variants'
  | 'DisplayPerspectives'
  | 'MainMenu'
  | 'Classes'
  | 'Students';

export type RoomSource = { roomSourceIndex: number };

export type UriTarget = { uri: string };

export type NamedTarget = { targetName: string };

export type CounterTarget = { targetCount: number };

export type TrackedProfileInputField = 'EditName' | 'EditMail';

export type LanguageOption = 'German' | 'English';

export type PiwikContentPiece =
  | 'Preview'
  | 'ImageRow'
  | 'Buttons'
  | 'Registration'
  | 'Login'
  | 'RegisterVerify'
  | 'ForgetPassword'
  | 'Account'
  | 'ManageAccount'
  | 'EditPassword'
  | 'DeleteAccount'
  | 'ManageFavorite'
  | 'EditFolder'
  | 'NewFolder'
  | 'NewVariante'
  | 'DeleteVariante'
  | 'CompareVariantes'
  | 'Perspectives'
  | 'BrandMenu'
  | 'Languages'
  | 'Themes'
  | 'Imprint'
  | 'Integrity'
  | 'Privacy'
  | 'Terms'
  | 'ChooseLanguage'
  | 'OpenIntegrityDocument'
  | 'RegisterSchool'
  | 'SchoolClasses'
  | 'SchoolStudents'
  | 'New'
  | TrackedProfileInputField
  | RoomSource;

export type PiwikContentTarget =
  | 'DesignMaterial'
  | 'Inspiration'
  | 'Close'
  | 'Open'
  | 'NewAccount'
  | 'Login'
  | 'ForgetPassword'
  | 'Reset'
  | 'Back'
  | 'Manage'
  | 'Logout'
  | 'Picture'
  | 'Cancel'
  | 'Submit'
  | 'EditPassword'
  | 'DeleteAccount'
  | 'Edit'
  | 'Delete'
  | 'EditFolder'
  | 'NewFolder'
  | 'Enable'
  | 'Disable'
  | 'InsertName'
  | 'Show'
  | 'Choose'
  | 'Light'
  | 'Dark'
  | 'AlreadyRegistered'
  | 'AddClass'
  | 'InviteStudent'
  | 'NewClass'
  | 'NewStudent'
  | 'UploadDocument'
  | TrackedProfileInputField
  | RoomSource
  | UriTarget
  | NamedTarget
  | CounterTarget
  | LanguageOption;

export const isRoomSource = (
  input: PiwikContentPiece | PiwikContentTarget,
): input is RoomSource => {
  return (input as RoomSource).roomSourceIndex !== undefined;
};

export const isUriTarget = (
  input: PiwikContentPiece | PiwikContentTarget,
): input is UriTarget => {
  return (input as UriTarget).uri !== undefined;
};

export const isNamedTarget = (
  input: PiwikContentTarget,
): input is NamedTarget => {
  return (input as NamedTarget).targetName !== undefined;
};

export const isCounterTarget = (
  input: PiwikContentTarget,
): input is CounterTarget => {
  return (input as CounterTarget).targetCount !== undefined;
};

export enum DataLayerVariableOptions {
  Version = 'version',
  Projects = 'projects',
  Favorites = 'favorites',
  DesignOrigin = 'designOrigin',
  Design = 'design',
  DesignName = 'designName',
  InspirationImage = 'inspirationImage',
  EditorProject = 'editorProject',
  Language = 'language',
  HarmonieName = 'harmonieName',
  Classes = 'classes',
  Students = 'students',
  Projectfolders = 'projectfolders',
}

export const mapMaterialTypeToCategory = (
  type: MaterialType,
): PiwikDesignOption | PiwikEventCategory => {
  switch (type) {
    case 'color_tint':
      return 'ColorTone';
    case 'floor_texture':
      return 'FloorCovering';
    case 'wall_texture':
      return 'WallCovering';
    case 'facade_texture':
      return 'FacadeCovering';
  }
};

export const mapFilterGroupTypeToAction = (
  group: string,
  activeFilterType?: MaterialType,
): PiwikEventAction | null => {
  switch (group) {
    case 'producer':
      return 'Producer';
    case 'collection':
      return 'Collection';
    case 'color':
      return 'Colorgroup';
    case 'SEARCH_CRITERIA_20':
      return 'Productgroup';
    case 'SEARCH_CRITERIA_39':
      return activeFilterType === 'floor_texture'
        ? 'Floor_Colorintensity'
        : 'Wallpaper_Colorintensity';
    case 'SEARCH_CRITERIA_36':
      return activeFilterType === 'floor_texture'
        ? 'Floor_Design'
        : 'Wallpaper_Design';
    case 'SEARCH_CRITERIA_37':
      return 'Floor_Material';
    case 'SEARCH_CRITERIA_38':
      return 'Floor_Use';
    default:
      return null;
  }
};

export const mapPathnameToPage = (
  pathname: TypedToOptions,
  search: Record<string, unknown>,
): { pageTitle: string; dimensions: DataLayerVariableOptions[] } => {
  const { user } = useAuthenticationStore.getState();

  const defaultDimensions = [
    DataLayerVariableOptions.Version,
    DataLayerVariableOptions.Language,
  ];

  switch (true) {
    case pathname === '/':
      return {
        pageTitle: 'Welcome Page',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.Projects,
          DataLayerVariableOptions.Favorites,
          DataLayerVariableOptions.Projectfolders,
        ],
      };
    case pathname === '/projects':
      return {
        pageTitle: 'Projects',
        dimensions: defaultDimensions,
      };
    case pathname === '/inspirations':
      return {
        pageTitle: 'Inspiration',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.DesignOrigin,
          DataLayerVariableOptions.Design,
          DataLayerVariableOptions.DesignName,
        ],
      };
    case pathname === '/collections':
      return {
        pageTitle: 'Collections',
        dimensions: defaultDimensions,
      };
    case pathname === '/collections/colors':
      return {
        pageTitle: 'Collections-Colortone',
        dimensions: defaultDimensions,
      };
    case pathname === '/collections/walls':
      return {
        pageTitle: 'Collections-Wallcovering',
        dimensions: defaultDimensions,
      };
    case pathname === '/collections/floors':
      return {
        pageTitle: 'Collections-Floorcovering',
        dimensions: defaultDimensions,
      };
    case pathname === '/collections/facades':
      return {
        pageTitle: 'Collections-Facadecovering',
        dimensions: defaultDimensions,
      };
    case pathname === '/favorites':
      return {
        pageTitle: 'Favorites',
        dimensions: defaultDimensions,
      };
    case pathname?.includes('/inspiration-project/'): {
      const { initialHarmonyIds } = useInspirationEditorStore.getState();
      if (initialHarmonyIds && initialHarmonyIds.length > 0) {
        return {
          pageTitle: 'DesignGuide-Editor',
          dimensions: [
            ...defaultDimensions,
            DataLayerVariableOptions.HarmonieName,
          ],
        };
      }
      return {
        pageTitle: 'Inspiration-Editor',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.DesignOrigin,
          DataLayerVariableOptions.Design,
          DataLayerVariableOptions.DesignName,
          DataLayerVariableOptions.InspirationImage,
        ],
      };
    }
    case pathname?.includes('/inspirations/') && !!search.harmonyId:
      return {
        pageTitle: 'DesignGuide-Editor',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.HarmonieName,
        ],
      };
    case pathname?.includes('/inspirations/'):
      return {
        pageTitle: 'Inspiration-Editor',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.DesignOrigin,
          DataLayerVariableOptions.Design,
          DataLayerVariableOptions.DesignName,
          DataLayerVariableOptions.InspirationImage,
        ],
      };
    case pathname?.includes('/editor'):
      return {
        pageTitle: 'Projects-Editor',
        dimensions: [
          ...defaultDimensions,
          DataLayerVariableOptions.EditorProject,
        ],
      };
    case pathname === '/pricing':
      return {
        pageTitle: 'Subscription Page',
        dimensions: defaultDimensions,
      };
    case pathname === '/designguide':
      if (!user) {
        return {
          pageTitle: 'DesignGuide-LogIn',
          dimensions: defaultDimensions,
        };
      }

      // eslint-disable-next-line no-case-declarations
      const { scene } = useDesignGuideStore.getState().selection;

      if (scene === 'interior') {
        return {
          pageTitle: 'DesignGuide-Interior',
          dimensions: defaultDimensions,
        };
      }

      return {
        pageTitle: 'DesignGuide-Exterior',
        dimensions: defaultDimensions,
      };
    case pathname === '/imprint':
      return {
        pageTitle: 'Imprint',
        dimensions: defaultDimensions,
      };
    case pathname === '/integrity':
      return {
        pageTitle: 'Integrity',
        dimensions: defaultDimensions,
      };
    case pathname === '/privacy':
      return {
        pageTitle: 'Privacy',
        dimensions: defaultDimensions,
      };
    case pathname === '/tos':
      return {
        pageTitle: 'Terms',
        dimensions: defaultDimensions,
      };
    case pathname === '/classes':
      return {
        pageTitle: 'SchoolClasses',
        dimensions: [...defaultDimensions, DataLayerVariableOptions.Classes],
      };
    case pathname === '/students':
      return {
        pageTitle: 'Students',
        dimensions: [...defaultDimensions, DataLayerVariableOptions.Students],
      };
    default:
      return {
        dimensions: [],
        pageTitle: capitalizeSentence(pathname?.replace('/', ' ') ?? ''),
      };
  }
};

export const mapAuthModalTypeToPiwikContentPiece = (
  authModalType: AuthModalType,
): PiwikContentPiece | undefined => {
  switch (authModalType) {
    case 'login':
      return 'Login';
    case 'register':
      return 'Registration';
    case 'passwordForgotten':
      return 'ForgetPassword';
    case 'registerOtp':
      return 'RegisterVerify';
    default:
      console.error('could not map modal type to piwik content piece');
      return undefined;
  }
};

export const setDataLayerValues = (
  dimensions: DataLayerVariableOptions[],
  pathname: string,
  documentTitle: string,
  projectCount: number | null = null,
  classesCount: number | null = null,
  studentsCount: number | null = null,
  projectfoldersCount: number | null = null,
  projectInspirationId: string | null = null,
) => {
  const newDataLayerVariables = dimensions.reduce<Record<string, string>>(
    (acc, dimension) => {
      switch (dimension) {
        case DataLayerVariableOptions.Version: {
          const { userRole } = useAuthenticationStore.getState();
          // ToDo: Capitalize first letter?
          acc[dimension] = userRole;
          break;
        }
        case DataLayerVariableOptions.Favorites: {
          const { userRole } = useAuthenticationStore.getState();

          if (userRole !== 'free') {
            const { favorites } = useFavoriteStore.getState();
            acc[dimension] = favorites.length.toString();
          }
          break;
        }
        case DataLayerVariableOptions.InspirationImage: {
          if (pathname.includes('/inspiration-project/')) {
            if (projectInspirationId != null) {
              acc[dimension] = projectInspirationId;
            }
          } else {
            const inspirationId = pathname.replace('/inspirations/', '');
            acc[dimension] = inspirationId;
          }
          break;
        }
        case DataLayerVariableOptions.DesignOrigin: {
          const { designOrigin } = usePiwikStore.getState();
          if (designOrigin != null) {
            acc[dimension] = designOrigin;
          }
          break;
        }
        case DataLayerVariableOptions.Design: {
          const { design } = usePiwikStore.getState();
          if (design != null) {
            acc[dimension] = design;
          }
          break;
        }
        case DataLayerVariableOptions.DesignName: {
          const { designName } = usePiwikStore.getState();
          if (designName != null) {
            acc[dimension] = designName;
          }
          break;
        }
        case DataLayerVariableOptions.HarmonieName: {
          const { harmonieName } = usePiwikStore.getState();
          if (harmonieName != null) {
            acc[dimension] = harmonieName;
          }
          break;
        }
        case DataLayerVariableOptions.Projects: {
          if (projectCount != null) {
            acc[dimension] = projectCount.toString();
          }
          break;
        }
        case DataLayerVariableOptions.EditorProject: {
          const editorProjectMode = window.location.search.includes(
            'projectId=',
          )
            ? 'Existing'
            : 'New';
          acc[dimension] = editorProjectMode;
          break;
        }
        case DataLayerVariableOptions.Language: {
          const language = useLocalizationStore.getState().language;
          acc[dimension] = t(`languages.${language}`, { lng: 'en' });
          break;
        }
        case DataLayerVariableOptions.Classes: {
          if (classesCount != null) {
            acc[dimension] = classesCount.toString();
          }
          break;
        }
        case DataLayerVariableOptions.Students: {
          if (studentsCount != null) {
            acc[dimension] = studentsCount.toString();
          }
          break;
        }
        case DataLayerVariableOptions.Projectfolders: {
          if (projectfoldersCount != null) {
            acc[dimension] = projectfoldersCount.toString();
          }
          break;
        }
      }
      return acc;
    },
    Object.fromEntries(
      Object.values(DataLayerVariableOptions).map((key) => [key, '']),
    ),
  );

  if (isInAppWebView) {
    trackInMobileAppPageView(newDataLayerVariables, documentTitle);
  } else {
    DataLayer.push({ event: 'page_info', page_info: newDataLayerVariables });
  }
};
let isInAppWebView = false;
export function setIsInAppWebView(value: boolean) {
  isInAppWebView = value;
}

export const parseContentPiece = (input: PiwikContentPiece) => {
  if (isRoomSource(input)) {
    return 'RoomSource' + input.roomSourceIndex.toString();
  } else {
    return input;
  }
};

export const parseContentTarget = (input: PiwikContentTarget) => {
  if (isUriTarget(input)) {
    return input.uri;
  } else if (isRoomSource(input)) {
    return 'RoomSource' + input.roomSourceIndex.toString();
  } else if (isNamedTarget(input)) {
    return input.targetName;
  } else if (isCounterTarget(input)) {
    return input.targetCount.toString();
  } else {
    return input;
  }
};

export const trackMaterialRendering = (
  editor: 'Inspiration' | 'Editor',
  material: Material,
) => {
  const materialType = material.type.includes('color_tint')
    ? 'Colortone'
    : 'Texture';
  trackEvent('Renderings', editor, materialType);
};

const dimensionMap: Record<DataLayerVariableOptions, number> = {
  version: 1,
  projects: 2,
  favorites: 3,
  designOrigin: 4,
  design: 5,
  designName: 6,
  inspirationImage: 7,
  editorProject: 8,
  language: 9,
  harmonieName: -1,
  classes: -2,
  students: -3,
  projectfolders: 14,
};

function getInMobileAppCustomDimensionId(
  name: DataLayerVariableOptions,
): number {
  return dimensionMap[name] ?? 0;
}

export const trackInMobileAppPageView = (
  customDimensions: Record<DataLayerVariableOptions, string>,
  documentTitle: string,
) => {
  const typedWindow = window as Window & {
    _paq?: Array<[string, ...unknown[]]>;
  };
  const _paq = (typedWindow._paq = typedWindow._paq || []);

  // test mit localhost:3000
  // _paq.push([
  //   'setCustomUrl',
  //   `https://develop.spectrum.dev.pcg.io${pathname}`,
  // ]);
  _paq.push(['setDocumentTitle', documentTitle]);

  (
    Object.keys(customDimensions) as Array<keyof typeof customDimensions>
  ).forEach((name) => {
    const idx = getInMobileAppCustomDimensionId(name);
    if (idx > 0) {
      _paq.push(['setCustomDimension', idx, customDimensions[name]]);
    }
  });

  _paq.push(['trackPageView']);

  (
    Object.keys(customDimensions) as Array<keyof typeof customDimensions>
  ).forEach((name) => {
    const idx = getInMobileAppCustomDimensionId(name);
    if (idx > 0) {
      _paq.push(['deleteCustomDimension', idx]);
    }
  });
};
